import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * 경로별 브라우저 탭 제목 · 검색 설명.
 *
 * 정적 SPA 라 카카오톡/슬랙 같은 링크 미리보기 봇은 index.html 의 기본 메타만 본다.
 * 여기 값은 브라우저 탭과 JS 를 실행하는 검색엔진(구글)용이다.
 */
const DEFAULT_TITLE = "Devit | 커피 한 잔 하고 오면, PR이 와 있습니다";

interface PageMeta {
  title: string;
  description?: string;
}

const PAGE_META: Record<string, PageMeta> = {
  "/": { title: DEFAULT_TITLE },
  "/pricing": {
    title: "가격 | Devit",
    description: "Devit 가격 안내. 지금은 모든 플랜을 무료로 사용할 수 있습니다.",
  },
  "/download": {
    title: "다운로드 | Devit",
    description:
      "macOS와 Windows용 Devit 데스크톱 앱을 내려받고, 시작에 필요한 준비물을 확인하세요.",
  },
  "/login": { title: "로그인 | Devit" },
  "/signup": { title: "회원가입 | Devit" },
  "/forgot-password": { title: "비밀번호 찾기 | Devit" },
  "/reset-password": { title: "비밀번호 재설정 | Devit" },
  "/verify-email": { title: "이메일 인증 | Devit" },
  "/invitations/accept": { title: "초대 수락 | Devit" },
  "/privacy": { title: "개인정보 처리방침 | Devit" },
  "/terms": { title: "이용약관 | Devit" },
  "/refund": { title: "환불 정책 | Devit" },
  "/contact": { title: "문의 | Devit" },
  "/app": { title: "대시보드 | Devit" },
  "/app/organizations": { title: "조직 | Devit" },
  "/app/projects": { title: "프로젝트 | Devit" },
  "/app/usage": { title: "사용량 | Devit" },
  "/app/billing": { title: "결제 | Devit" },
  "/app/account": { title: "계정 | Devit" },
  "/app/admin/releases": { title: "릴리스 관리 | Devit" },
  "/app/admin/pricing": { title: "가격 관리 | Devit" },
  "/app/admin/users": { title: "사용자 관리 | Devit" },
};

const NOT_FOUND_META: PageMeta = { title: "페이지를 찾을 수 없음 | Devit" };

/** 라우트가 바뀔 때마다 document.title 과 meta description 을 맞춘다. App 에서 1회 호출. */
export function usePageMeta(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
    const meta = PAGE_META[normalized] ?? NOT_FOUND_META;
    document.title = meta.title;
    // index.html 의 기본 설명을 기억해 두었다가 전용 설명이 없는 페이지에서 되돌린다.
    const tag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!tag) return;
    tag.dataset.default ??= tag.content;
    tag.content = meta.description ?? tag.dataset.default;
  }, [pathname]);
}
